<template>
  <div class="audience-form">
    <form @submit.prevent="startGame()">
      <div class="input-label">
        {{ $t("enterYourName") }}
      </div>
      <div class="item-input" :class="{error: error}">
        <input
          type="text"
          :maxlength="maxInputLength"
          v-model="audienceName"
          class="item-input-text"
          @input="onChangeAudienceName()"
        >
        <div class="item-input-limit" v-if="$store.state.audienceName.trim().length === 0">
          {{ maxInputLength - audienceName.length }}
        </div>
      </div>
      <div class="text-left error-field text-danger" v-if="error">
        {{ $t('mandatoryFieldError') }}
      </div>
      <!-- If presentation enables team play -->
      <div v-if="$store.state.presentation.teamPlay && slide.type !== 'spinnerWheel'">
        <div class="input-label">
          {{ $t('pickYourTeam') }}
        </div>
        <div class="item-input">
          <div
            class="fake-input"
            @click="toggleSearchTeam()"
            :class="{active: audienceQuizTeam, danger: invalidAudienceTeam || teamFull}"
          >
            <span v-if="!audienceQuizTeam">Select your team</span>
            <div v-if="audienceQuizTeam" class="overflow-hidden mw-100">
              {{ audienceQuizTeam.name }}
            </div>
            <i class="fas fa-chevron-down" />
          </div>
          <div
            class="text-left text-danger invalid-team-note"
            v-if="teamFull"
          >
            {{ $t('teamIsFull') }}
          </div>
          <div class="text-left text-danger invalid-team-note" v-if="invalidAudienceTeam">
            {{ $t('mandatoryFieldError') }}
          </div>
        </div>
      </div>

      <div v-if="isShownEmojiAvatar" class="input-label">
        {{ $t("chooseEmoji") }}
      </div>
      <div v-if="isShownEmojiAvatar" class="item-input">
        <div class="pick-emoji" v-click-outside="clickOutSideEmoji">
          <div class="horizontal layout center" @click="openPickEmoji()" :class="{'disable-emoji': $store.state.isAudienceEmojiDisabled}">
            <div class="emoji-picked">
              {{ $store.state.audienceEmoji }}
            </div>
            <div
              v-if="!openEmoji"
              class="text-action"
              :class="{'white': isTextColourLight}"
            >
              {{ $t("changeButton") }}
            </div>
            <div
              v-if="openEmoji"
              class="text-action"
              :class="{'white': isTextColourLight}"
            >
              {{ $t("closeAvatar") }}
            </div>
          </div>
          <picker
            :data="emojiIndex"
            set="emojione"
            v-show="openEmoji"
            @select="addEmoji"
            v-if="emojiIndex"
            :native="true"
          />
        </div>
      </div>

      <div>
        <aha-antd-button
          variant="primary-alt"
          block
          size="lg"
          type="submit"
          :submitting="submiting || checkingTeamFull"
          isLoadingInline
        >
          <span>{{ $t("joinTheGameButton") }}</span>
        </aha-antd-button>
      </div>
      <div class="text-center text-danger padding" v-if="textError">
        {{ textError }}
      </div>
    </form>

    <div class="backdrop" v-if="openSelectTeam" @click="toggleSearchTeam()" />
    <div
      class="box-search-team animate__animated animate__slideInUp animate__faster"
      v-if="openSelectTeam"
    >
      <div class="content-box-search-team">
        <div class="item-input">
          <input
            type="text"
            placeholder="Search..."
            class="item-input-text"
            v-model="teamSearchText"
            maxlength="20"
          >
          <div class="item-input-limit">
            {{ 20 - teamSearchText.length }}
          </div>
        </div>
        <div class="list-team">
          <div
            class="team-item horizontal layout center"
            v-for="(item, index) in _filterTeam()"
            :key="index"
            @click="selectTeam(item)"
            v-show="item.visible"
          >
            <div class="index">
              {{ item.id }}.
            </div>
            <div class="team-color" :style="{'background-color': getColor(item) }" />
            <div class="audience-flex team-name">
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import data from '@/assets/data/all.json'
import storage from '@/services/storage'
import Config from '@/config'
import Util from '@/util'
import * as Sentry from '@sentry/vue'
import EventBus from '@/eventBus'
import Constant from '@/constant'

// UI
import { mapState, mapGetters } from 'vuex'
import QuizStatuses from '@/constants/QuizStatuses'
import audienceForm from '@/services/audienceForm'
import joinPresentationUtils from '@/utils/join-presentation.utils'

const { maxInputLength } = Constant

export default {
  components: {
    Picker,
  },
  data() {
    return {
      error: false,
      invalidAudienceTeam: false,
      audienceName: '',
      openEmoji: false,
      submiting: false,
      emojiIndex: null,
      data: {},
      loading: true,
      noResult: false,
      audienceQuizTeam: null,
      openSelectTeam: false,
      teamSearchText: '',
      openTeamList: false,
      checkingTeamFull: false,
      teamFull: false,
      textError: '',
      maxInputLength,
    }
  },
  computed: {
    ...mapState({
      audienceId: (state) => state.audienceId,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapGetters('themeModule', ['isTextColourLight']),
    ...mapGetters('presentationSettings', ['isShownEmojiAvatar']),
  },
  mounted() {
    this.emojiIndex = new EmojiIndex(data)
    document.body.classList.add('hide-qa-button')
    this.audienceName = this.$store.state.audienceName
    if (!this.$store.state.audienceName && this.currentUser && this.currentUser.id) {
      this.audienceName = this.currentUser.firstName
    }
    EventBus.$on('prompt-audience-name', () => {
      this.error = true
    })
  },
  destroyed() {
    this.$store.state.disableScrollBody = false
    document.body.classList.remove('hide-qa-button')
  },
  methods: {
    getColor(team) {
      return team.color || Util.getAhaColor(team.id)
    },
    selectTeam(item) {
      if (this.checkingTeamFull) return
      this.audienceQuizTeam = item
      this.invalidAudienceTeam = false
      this.toggleSearchTeam()
    },
    toggleSearchTeam() {
      this.openSelectTeam = !this.openSelectTeam
      if (!this.openSelectTeam) {
        this.$store.state.disableScrollBody = false
      } else {
        this.$store.state.disableScrollBody = true
      }
    },
    onChangeAudienceName() {
      this.error = false
    },
    _filterTeam() {
      if (!this.teamSearchText) {
        return this.$store.state.presentation.teamData
      }
      const keyword = this.teamSearchText.toLowerCase()
      return this.$store.state.presentation.teamData.filter((item) => {
        return item.name.toLowerCase().indexOf(keyword) !== -1
      })
    },

    clickOutSideEmoji() {
      if (this.openEmoji) {
        this.openEmoji = false
      }
    },
    openPickEmoji() {
      if (this.$store.state.isAudienceEmojiDisabled) return
      this.openEmoji = !this.openEmoji
    },
    addEmoji(emoji) {
      this.$store.state.audienceEmoji = emoji.native
      this.openEmoji = false
    },
    async startGame() {
      this.invalidAudienceTeam = false
      if (this.$store.state.presentation.teamPlay && this.slide.type !== 'spinnerWheel') {
        this.checkingTeamFull = true
        this.textError = ''
        if (!this.audienceQuizTeam) {
          this.invalidAudienceTeam = true
          this.checkingTeamFull = false
          return
        }
        try {
          const resCheckTeamFull = await this.$http.post(Config.api.checkQuizTeamMember, {
            teamSize: this.$store.state.presentation.teamSize,
            presentationId: this.$store.state.presentation.id,
            c: this.$store.state.presentation.uniqueAccessCode,
            audiencePacing: this.$store.state.presentation.audiencePacing,
            t: this.audienceQuizTeam.id,
          })
          this.teamFull = resCheckTeamFull.body.full
          this.checkingTeamFull = false
        } catch (error) {
          Sentry.captureException(error)
          if (!error.status) {
            this.textError = 'Cannot connect to the server. Please try again.'
          } else {
            this.audienceQuizTeam = null
          }
          this.checkingTeamFull = false
        }

        if (this.checkingTeamFull || this.teamFull) {
          return
        }
      }
      this.error = false
      this.invalidAudienceTeam = false
      const { presentation, audienceEmoji } = this.$store.state

      if (!this.audienceName.trim()) {
        this.error = true
        return
      }
      if (this.$store.state.presentation.teamPlay && !this.audienceQuizTeam && this.slide.type !== 'spinnerWheel') {
        this.invalidAudienceTeam = true
        return
      }
      storage.setValueByPresentation(
        presentation.id,
        'audienceEmoji',
        audienceEmoji,
      )
      storage.setValueByPresentation(
        presentation.id,
        'audienceName',
        this.audienceName,
      )
      // If presentation team play
      if (this.$store.state.presentation.teamPlay && this.slide.type !== 'spinnerWheel') {
        storage.setValueByPresentation(
          presentation.id,
          'audienceQuizTeam',
          this.audienceQuizTeam.id,
        )
      }

      this.$store.state.isAudienceEmojiDisabled = true
      this.submiting = true
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      const payload = {
        a: this.audienceId,
        n: this.audienceName,
        e: this.$store.state.audienceEmoji,
        c: this.$store.state.presentation.uniqueAccessCode,
        q: true,
      }

      if (this.audienceQuizTeam) {
        payload.t = this.audienceQuizTeam.id
      }

      try {
        await this.$http.post(joinPresentationUtils.getApiUrlJoinPresentation(), payload)
        if (this.slide.quizStatus === QuizStatuses.RESULT_SCREEN) {
          this.loading = false
          EventBus.$emit('quizGetResult')
        }
        this.submiting = false
        this.$store.state.audienceName = this.audienceName
        if (this.$store.state.presentation.teamPlay && this.slide.type !== 'spinnerWheel') {
          this.$store.state.audienceQuizTeam = this.audienceQuizTeam.id
        }
        this.$emit('joinGame')
        EventBus.$emit('confirmNextSlide')

        Util.mixpanelTrack(
          'Audience > Quiz',
          'Form Join Quiz',
          'Audience Joined Quiz',
        )

        audienceForm.updateAudienceData()
      } catch (error) {
        Sentry.captureException(error)
        this.textError = this.$t('disconnectError')
        this.submiting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.disable-emoji {
  cursor: not-allowed;
}
.backdrop {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  top: 0;
  position: fixed;
  background-color: #0000005c;
  left: 0;
  z-index: 10;
}

.box-search-team {
  .item-input-text {
    height: 44px;
  }
  .item-input-limit {
    top: 16px;
  }

  .team-name {
    text-align: left;
    word-break: break-word;
  }

  .content-box-search-team {
    padding: 10px;
    background-color: #fff;
    max-width: 680px;
    margin: 0 auto;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
    height: 100%;
    .list-team {
      overflow-y: scroll;
      height: calc(100% - 44px - 16px);
      margin-top: 16px;
    }
  }
}

.team-item {
  font-weight: bold;
  min-height: 48px;
  border-bottom: 1px solid #ddd;
  cursor: pointer;

  .index {
    font-weight: normal;
    min-width: 45px;
    padding: 0 8px 0 6px;
    text-align: right;
  }

  .team-color {
    width: 18px;
    height: 18px;
    background-color: black;
    border-radius: 3px;
    margin-right: 8px;
  }

  .form-control {
    height: 36px;
    border-radius: 5px;
    margin-right: 8px;

    &.error {
      border-color: $danger-color;
    }
  }

  button {
    width: 100px;
  }
}

.box-search-team {
  position: fixed;
  bottom: 0;
  width: 100%;
  color: #333;
  height: 500px;
  max-height: 100%;
  padding-top: 12px;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  z-index: 999;
  left: 0;
}
.fake-input {
  width: 100%;
  height: 52px;
  border-radius: 8px;
  box-shadow: none;
  border: 2px solid #ddd;
  font-size: 18px;
  padding: 0px 40px 0 16px;
  -webkit-appearance: none;
  outline: none;
  line-height: normal;
  box-sizing: border-box;
  line-height: 49px;
  background-color: #fff;
  color: #afafaf;
  position: relative;
  cursor: pointer;
  text-align: left;

  &.danger {
    border-color: $danger-color;
  }

  &.active {
    color: #333;
  }

  .loading-spinner, .fas {
    position: absolute;
    right: 10px;
    top: 14px;
    color: #333;
  }
}
.audience-form {
  .item-input {
    position: relative;
    margin-bottom: 16px;
    .invalid-team-note {
      padding-top: 7px;
    }
    .item-input-icon {
      cursor: pointer;
    }
    .filter-team-list-wrapper {
      .item-input-text {
        cursor: pointer;
        &.team-list-show {
          cursor: text;
        }
      }
    }
    .team-list {
      position: absolute;
      overflow: auto;
      border: 1px solid #ddd;
      z-index: 1;
      background-color: #ffffff;
      width: 100%;
      margin-top: 1px;
      padding-top: 10px;
      cursor: pointer;
      .team-listing {
        display: block;
        padding: 5px 16px;
        &:hover {
          background-color: theme('colors.primary.90');
          color: #ffffff;
        }
      }
    }
  }
  .pick-emoji {
    position: relative;
    cursor: pointer;
    width: fit-content;
    -webkit-tap-highlight-color: transparent;

    .emoji-mart {
      position: absolute;
      top: 90px;
      left: 0;
      z-index: 110;
    }
  }
  .emoji-picked {
    font-size: 80px;
    margin: 16px;
  }
  .text-action {
    color: $primary-color;

    &.white {
      color: #fff;
      text-decoration: underline;
    }
  }
  .button-submit {
    .loading-spinner {
      margin-bottom: 0px;
    }
  }
}
.input-label {
  text-align: left;
}

.error-field {
  margin-bottom: 8px;
  margin-top: -8px;
}
</style>
